// The list's toolbar (SPEC-viewfinder §2.2, §2.3). One row, 44 px tall, at the
// head of the list and ABOVE the rail's scroll container — so it never scrolls
// away, and so the sticky kreska keeps the rail's top edge to itself and §5.3's
// anchoring arithmetic is untouched.
//
// Two halves, and only one of them is a control. „w promieniu 12 · poza 23" is
// plain information (owner, 2026-09-21: „zostaje zwykłą informacją na razie") —
// text, not buttons, not focusable. „układ: czas · odległość" is one group with
// two real buttons and one tab stop, between the slider/crosshair group and the
// rail (D-f).
import { derived, type DerivedState } from '../derived.ts';
import { mount } from '../shell.ts';
import { gesture } from '../state/gesture.ts';
import { viewState, type Order } from '../state/viewstate.ts';
import { el } from './rows.ts';

/** The order group's accessible name (§5). The buttons wear their own words. */
const GROUP = 'układ listy';

const OPTIONS: { order: Order; label: string }[] = [
  { order: 'czas', label: 'czas' },
  { order: 'odleglosc', label: 'odległość' },
];

/**
 * A count, as the toolbar sets it: the words in Archivo and the number in
 * JetBrains Mono, both at 12 px.
 *
 * This is the one disclosed deviation from the approved mock-up (ruling 5a).
 * #238's final shape said the row was mono throughout; set that way the
 * owner's own string measures 349,81 px against about 320 px of content on a
 * 360 px phone, and all-mono simply does not fit. Mixed, the same worst case is
 * 269,43 px — which is also the Papier rule the rest of the app already
 * follows, numbers in mono and words in Archivo.
 */
function count(word: string, n: number): HTMLElement {
  const box = el('span', 'toolbar__count');
  box.append(`${word} `, el('span', 'mono', String(n)));
  return box;
}

/** A separator that is drawn and never read: the `·` is punctuation, not a word. */
function dot(): HTMLElement {
  const glyph = el('span', 'toolbar__dot', ' · ');
  glyph.setAttribute('aria-hidden', 'true');
  return glyph;
}

/**
 * The order group. Two buttons rather than one toggle, because a toggle names
 * one of the two states and a resident reading „czas" cannot tell whether that
 * is what the list IS or what it would become. `aria-pressed` on each says
 * which one stands, and the current one is bold `--ink` against the other's
 * `--ink2` — no borders, no tab bar, no new colour.
 */
function orderGroup(onPick: (order: Order) => void): {
  node: HTMLElement;
  paint: (current: Order) => void;
} {
  const node = el('div', 'toolbar__order');
  node.setAttribute('role', 'group');
  node.setAttribute('aria-label', GROUP);
  node.append(el('span', 'toolbar__order-label', 'układ:'));

  const buttons = OPTIONS.map(({ order, label }, i) => {
    const button = el('button', 'toolbar__option', label);
    button.type = 'button';
    button.dataset.order = order;
    button.addEventListener('click', () => {
      onPick(order);
    });
    if (i > 0) node.append(dot());
    node.append(button);
    return { order, button };
  });

  return {
    node,
    paint: (current) => {
      for (const { order, button } of buttons) {
        button.setAttribute('aria-pressed', String(order === current));
      }
    },
  };
}

/**
 * Whether the resident asked for this order BY NAME, rather than arriving on it
 * from a link or from what this browser remembers. §2.3 puts the rail at the
 * top after a named re-order, like the „starsze" fold; arriving on an order is
 * not a re-order and moves nothing.
 *
 * A module-level flag rather than a parameter: the toolbar writes the state and
 * the rail reads the derivation that write produces, and they meet nowhere
 * else. It is consumed by the very next render, exactly as `foldMoved` is.
 */
let asked = false;
export const orderWasAsked = (): boolean => {
  const was = asked;
  asked = false;
  return was;
};

export function mountToolbar(): void {
  const root = mount('toolbar');
  const counts = el('p', 'toolbar__counts');
  const inside = count('w promieniu', 0);
  const outside = count('poza', 0);
  counts.append(inside, dot(), outside);

  const group = orderGroup((order) => {
    if (viewState.get().order === order) return;
    asked = true;
    // `'replace'`: the order changes the view without opening anything, which
    // is the same class as the radius slider (§2.3, ADR-0009).
    viewState.set({ order }, 'replace');
  });

  root.append(counts, group.node);

  /**
   * The two numbers, from whichever authority is standing — and only when they
   * have moved. Under a finger this is asked on every frame, and rebuilding two
   * spans sixty times a second to write the same digits is work the budget has
   * no room for (§4.3).
   */
  let said = '';
  const say = (n: number, m: number) => {
    if (`${n} ${m}` === said) return;
    said = `${n} ${m}`;
    inside.replaceChildren(...count('w promieniu', n).childNodes);
    outside.replaceChildren(...count('poza', m).childNodes);
  };

  derived.subscribe((state: DerivedState) => {
    group.paint(state.view.order);
    // Under the finger the counts are the gesture's, not the derivation's: a
    // poll that lands mid-gesture must not put the settled numbers back (§4.1).
    if (gesture.active()) return;
    const axis = state.derived;
    // Before the first snapshot there is nothing counted and nothing to say
    // about it; the row keeps its height so the rail does not jump when the
    // numbers arrive.
    say(axis?.placed.length ?? 0, axis?.beyond.length ?? 0);
  });

  // „Poza M" follows the ring under the finger, and it counts the SAME array
  // the circle draws as dots (§3) — `remeasure` divides one set and hands both
  // halves out, so the number and the picture cannot come apart.
  gesture.subscribe((live) => {
    if (live) say(live.placed.length, live.beyond.length);
    else say(derived.get().derived?.placed.length ?? 0, derived.get().derived?.beyond.length ?? 0);
  });
}
